/**
 * Paylaşılan **donut grafik** — bağımlılıksız SVG (Reuse Mandate).
 *
 * Projede grafik kütüphanesi yoktur ve eklenmez: PrimeReact `Chart` `chart.js` peer-dependency'sini ister
 * ve iki basit görselleştirme için tam bir canvas motoru ölçüsüz olurdu. SVG kalması üç şeyi bedava verir:
 * renkler `var(--*)` token'larıyla doğrudan gelir, tema değişimi ek iş gerektirmez ve grafik test
 * edilebilir/erişilebilir kalır (canvas'ta üçü de mümkün değil).
 *
 * Kurallar:
 * - `value = 0` olan dilim **çizilmez** (sıfır uzunlukta dilim/gürültü üretilmez).
 * - Merkezdeki sayı **`total` prop'undan** gelir; bileşen dilimleri yeniden toplaMAZ (sunucu otoritesi).
 * - Renk **yalnız** çağıranın verdiği token değerlerinden; bileşen içinde sabit renk yoktur.
 * - `role="img"` + `ariaLabel`; her dilimde `<title>` → renk tek bilgi taşıyıcısı değildir (legend'da
 *   adet/yüzde metin olarak da bulunur).
 *
 * **Seçim (077 §K3; 2026-09-16'dan beri ÇOKLU):** `onSelect` verilirse dilimler tıklanabilir olur;
 * `selectedKeys` boş değilken listedeki dilimler tam opaklıkta kalır, diğerleri soluklaşır.
 * `partialKeys`, alt kırılımlarının yalnız bir bölümü seçili olan dilimlerdir: solmazlar ama tam
 * seçiliden de ayrışırlar — aksi hâlde "grubun tamamı" ile "grubun yarısı" aynı görünürdü.
 * Erişilebilirliğin taşıyıcısı SVG **değildir** —
 * klavye ve ekran okuyucu yolu çağıranın legend'ındaki gerçek `<button>`'lardır; buradaki tıklama yalnız
 * fare kullanıcısı için bir kısayoldur. Bu yüzden dilimlere `role="button"`/`tabIndex` verilmez:
 * "erişilebilir gibi görünen ama olmayan" bir hedef üretmek, hiç vermemekten kötüdür.
 */
export interface DonutSlice {
  /** Kararlı anahtar (API'nin `bucket` değeri) — renk/sıra eşlemesi buna bağlanır, ada değil. */
  key: string
  label: string
  value: number
  /** CSS renk değeri; çağıran token'dan verir (ör. `var(--act-delayed)`). */
  color: string
  /**
   * Dilimin **üzerine** yazılacak kısa metin (079 — kullanıcı isteği: "statü oranları grafik üstünde
   * gösterilecektir"), ör. `"%40,8"`. Biçimlendirme (ondalık ayracı, dil) çağıranın işidir.
   *
   * Metin `aria-hidden`'dır: aynı bilgi legend'da zaten metin olarak bulunur, iki kez okutmak gürültü
   * olurdu. Yazı **okunamayacak** durumdaysa hiç çizilmez (bkz. {@link MIN_BADGE_ARC} ve
   * {@link MIN_BADGE_SPACING}) — değer legend'dan yine okunur.
   */
  badge?: string
}

/**
 * Etiket yazılabilmesi için dilimin kendi **yayının** (px) en az bu kadar olması gerekir. Bunun altında
 * yazı kendi diliminden o kadar taşar ki hangi dilime ait olduğu okunamaz hâle gelir.
 *
 * <p>Eşik neden **yüzde değil piksel** (2026-08-07): oran eşiği (önceki `%6`) halkanın ölçüsünden bağımsız
 * olduğu için kalınlık/çap değiştiğinde sessizce yanlışa düşüyordu — ve "Güncellenen" gibi orta boy bir
 * dilimi, yazısı rahat sığdığı hâlde eliyordu. Yay uzunluğu doğrudan ölçülebilen büyüklüktür.
 */
const MIN_BADGE_ARC = 20

/**
 * İki komşu etiketin **merkezleri** arasında gereken en küçük yay (px). Yazı ~32px genişliğinde olduğu için
 * bundan yakın iki etiket harf harf üst üste biner ve <b>ikisi birden</b> okunamaz olur — dar dilimin
 * etiketini hiç yazmamak, iki etiketi birden kaybetmekten iyidir. Değer o durumda da legend'dan okunur.
 */
const MIN_BADGE_SPACING = 34

/**
 * Halkanın varsayılan **kalınlığı** ve dış ölçüsü. Kalınlık, üzerine yazılan yüzdeyi (11px) tamamen
 * içine alacak kadar geniş olmalıdır: saat 3/9 civarındaki dilimlerde yazı **ışınsal** yönde yer kaplar,
 * yani kalınlıktan taşan her piksel halkanın dışına çıkar. 26px'te "%40,8" iki yandan da taşıyordu
 * (kullanıcı geri bildirimi 2026-08-07).
 *
 * <p>Dış ölçü kalınlıkla birlikte büyür; aksi hâlde ortadaki delik (`size - 2 × thickness`) küçülür ve
 * merkezdeki toplam sayı ile "TOPLAM" etiketi halkanın altına girer. 180/40 → delik 100px kalır: dört
 * haneli bir toplam (28px başlık yüzü) hâlâ rahat sığar.
 *
 * <p><b>Varsayılan olması bilinçlidir</b>: rozetsiz çağıranlar da (Raporum'un delegasyon donut'u) bu
 * ölçüye gelir. Gerekçe orada rozet değil <b>tutarlılıktır</b> — iki donut aynı kartta yan yana
 * durabiliyor ve farklı kalınlıkta iki halka, aynı türden iki grafiği ilgisiz gösterirdi.
 */
const DEFAULT_THICKNESS = 40
const DEFAULT_SIZE = 180

export interface DonutChartProps {
  slices: DonutSlice[]
  /** Merkezdeki büyük sayı (sunucudan gelen toplam). */
  total: number
  /** Merkezdeki etiket, ör. "TOPLAM". */
  totalLabel: string
  /** Grafiğin erişilebilir adı. */
  ariaLabel: string
  size?: number
  thickness?: number
  /** Seçili dilim anahtarları (çoklu); boş değilse listede olmayan dilimler soluklaşır. */
  selectedKeys?: readonly string[]
  /** **Kısmen** seçili dilimler — solmazlar, ama tam seçililerden görsel olarak ayrışırlar. */
  partialKeys?: readonly string[]
  /** Dilim tıklaması; verilmezse grafik tamamen statiktir (068 davranışı korunur). */
  onSelect?: (key: string) => void
}

export default function DonutChart({
  slices,
  total,
  totalLabel,
  ariaLabel,
  size = DEFAULT_SIZE,
  thickness = DEFAULT_THICKNESS,
  selectedKeys = [],
  partialKeys = [],
  onSelect,
}: Readonly<DonutChartProps>) {
  const drawn = slices.filter((s) => s.value > 0)
  const sum = drawn.reduce((acc, s) => acc + s.value, 0)
  const radius = (size - thickness) / 2
  const circumference = 2 * Math.PI * radius
  const center = size / 2
  const selectable = onSelect != null

  // Dilimler saat 12'den başlayıp saat yönünde dizilir; her dilim kendi uzunluğu kadar boyanır ve
  // önceki dilimlerin toplamı kadar kaydırılır (stroke-dashoffset).
  let consumed = 0

  return (
    <svg
      className={`ui-donut${selectable ? ' ui-donut--selectable' : ''}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel}
    >
      <circle
        className="ui-donut-track"
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={thickness}
      />
      {sum > 0 &&
        drawn.map((slice) => {
          const length = (slice.value / sum) * circumference
          const offset = consumed
          consumed += length
          // Solma ölçütü "seçimde yok" değil, "seçimden HİÇ pay almıyor"dur: kısmen seçili bir dilim
          // seçili satırlar içerir, onu soluklaştırmak kullanıcıya o grupta hiç kayıt yokmuş gibi görünürdü.
          const partial = partialKeys.includes(slice.key)
          const dimmed =
            selectedKeys.length + partialKeys.length > 0 &&
            !partial &&
            !selectedKeys.includes(slice.key)
          return (
            <circle
              key={slice.key}
              className={`ui-donut-slice${dimmed ? ' ui-donut-slice--dim' : ''}${partial ? ' ui-donut-slice--partial' : ''}`}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={slice.color}
              strokeWidth={thickness}
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${center} ${center})`}
              onClick={selectable ? () => onSelect(slice.key) : undefined}
            >
              <title>{`${slice.label}: ${slice.value}`}</title>
            </circle>
          )
        })}
      {/* Yüzde etiketleri dilimlerin ÜSTÜNE, hepsi çizildikten sonra konur — aynı `<svg>` içinde sonra
          gelen düğüm üstte kalır; aksi hâlde bir sonraki dilimin kalın kenarı yazının üstünü örterdi. */}
      {sum > 0 &&
        badgesOf(drawn, sum, circumference).map(({ slice, midFraction }) => {
          const angle = midFraction * 2 * Math.PI - Math.PI / 2 // 0 = saat 12 (dilimlerle aynı başlangıç)
          return (
            <text
              key={`badge-${slice.key}`}
              className="ui-donut-badge"
              x={center + radius * Math.cos(angle)}
              y={center + radius * Math.sin(angle)}
              textAnchor="middle"
              dominantBaseline="central"
              aria-hidden="true"
            >
              {slice.badge}
            </text>
          )
        })}
      <text className="ui-donut-total" x={center} y={center} textAnchor="middle">
        {total}
      </text>
      <text className="ui-donut-label" x={center} y={center + 18} textAnchor="middle">
        {totalLabel}
      </text>
    </svg>
  )
}

/**
 * Etiketi yazılabilecek dilimler + her birinin **orta noktasının** çember üzerindeki payı (0–1). Pay,
 * dilimlerin çizim sırasıyla birebir aynı biriktirmeden gelir → yazı daima kendi diliminin ortasına oturur.
 *
 * <p>İki ayrı elemede bulunur: dilim kendi etiketini taşıyamayacak kadar darsa ({@link MIN_BADGE_ARC}) ve
 * etiket bir öncekine değecek kadar yakınsa ({@link MIN_BADGE_SPACING}). Ayrım önemlidir: <b>tek başına
 * duran</b> dar bir dilim etiketini KORUR — komşusu uzaktaysa yazı okunur ve onu elemek bilgiyi boş yere
 * gizlemek olur.
 */
function badgesOf(
  drawn: DonutSlice[],
  sum: number,
  circumference: number,
): Array<{ slice: DonutSlice; midFraction: number }> {
  const result: Array<{ slice: DonutSlice; midFraction: number }> = []
  let consumed = 0
  let lastCenter: number | null = null
  for (const slice of drawn) {
    const arc = (slice.value / sum) * circumference
    const center = consumed + arc / 2
    consumed += arc
    if (slice.badge == null || arc < MIN_BADGE_ARC) continue
    if (lastCenter != null && center - lastCenter < MIN_BADGE_SPACING) continue
    result.push({ slice, midFraction: center / circumference })
    lastCenter = center
  }
  dropWrapCollision(result, circumference)
  return result
}

/**
 * Halka **kapanırken** ilk ve son etiket de komşudur (saat 12 hizasının iki yanı) — sıralı tarama bu çifti
 * göremez. Çakışıyorlarsa **küçük dilimin** etiketi düşer: büyük dilimin yazısı görsel olarak daha çok
 * hak eder ve onu atmak grafiğin ana okumasını bozar.
 */
function dropWrapCollision(
  badges: Array<{ slice: DonutSlice; midFraction: number }>,
  circumference: number,
): void {
  if (badges.length < 2) return
  const first = badges[0]
  const last = badges[badges.length - 1]
  const gap = (1 - last.midFraction + first.midFraction) * circumference
  if (gap >= MIN_BADGE_SPACING) return
  badges.splice(first.slice.value < last.slice.value ? 0 : badges.length - 1, 1)
}
